const MOBILE_RESTORE_STORAGE_KEY = 'predictiveSearchMobileRestore';
const MOBILE_RESTORE_MAX_AGE = 10 * 60 * 1000;
const MOBILE_RESTORE_REAPPLY_DELAYS = [100, 350, 800];
const MOBILE_SEARCH_MEDIA_QUERY =
  '(max-width: 749px), (min-width: 750px) and (max-width: 970px) and (max-height: 749px) and (orientation: landscape) and (hover: none) and (pointer: coarse)';

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.closedByPageBlur = false;
    this.mobileRestoreQueryFrame = null;
    this.mobileRestoreQueryTimers = [];
    this.mobileModalCloseButton = null;
    this.mobileViewportListenersActive = false;
    this.mobileViewportUpdateFrame = null;
    this.onMobileModalCloseButtonClick = this.onMobileModalCloseButtonClick.bind(this);
    this.onMobileOverlayPointerDown = this.onMobileOverlayPointerDown.bind(this);
    this.onMobileViewportChange = this.onMobileViewportChange.bind(this);
    this.onPageBlur = this.onPageBlur.bind(this);
    this.onPageHide = this.onPageHide.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onSearchQueryTriggerClick = this.onSearchQueryTriggerClick.bind(this);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('click', this.onSearchQueryTriggerClick);
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('pointerdown', this.onMobileOverlayPointerDown);
    window.addEventListener('blur', this.onPageBlur);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  getQuery() {
    return this.input.value.trim();
  }

  isMobileSearch() {
    return window.matchMedia(MOBILE_SEARCH_MEDIA_QUERY).matches && this.matches('predictive-search.search-modal__form');
  }

  isMobileOverlaySearch() {
    return this.isMobileSearch() && this.classList.contains('predictive-search--mobile-overlay');
  }

  getSearchResultsCacheKey(searchTerm) {
    return searchTerm.replace(' ', '-').toLowerCase();
  }

  saveMobileRestoreState() {
    if (!this.isMobileSearch()) return;

    const query = this.getQuery();
    const hasResults =
      this.getAttribute('results') === 'true' || Boolean(this.querySelector('#predictive-search-results-groups-wrapper'));

    if (!query.length || (!this.isMobileOverlaySearch() && !hasResults)) return;

    try {
      window.sessionStorage.setItem(
        MOBILE_RESTORE_STORAGE_KEY,
        JSON.stringify({
          inputId: this.input.id || '',
          query,
          timestamp: Date.now(),
          url: window.location.href,
        })
      );
    } catch (error) {
      // Ignore unavailable sessionStorage, for example in restricted browsing modes.
    }
  }

  clearMobileRestoreState() {
    this.clearMobileRestoreQueryReapply();

    try {
      window.sessionStorage.removeItem(MOBILE_RESTORE_STORAGE_KEY);
    } catch (error) {
      // Ignore unavailable sessionStorage, for example in restricted browsing modes.
    }
  }

  getMobileRestoreState() {
    let restoreState;

    try {
      restoreState = JSON.parse(window.sessionStorage.getItem(MOBILE_RESTORE_STORAGE_KEY) || 'null');
    } catch (error) {
      this.clearMobileRestoreState();
      return null;
    }

    if (!restoreState?.query || restoreState.url !== window.location.href) return null;

    if (Date.now() - Number(restoreState.timestamp || 0) > MOBILE_RESTORE_MAX_AGE) {
      this.clearMobileRestoreState();
      return null;
    }

    return restoreState;
  }

  isHistoryRestore(event) {
    if (event?.persisted) return true;

    const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
    return navigationEntry?.type === 'back_forward';
  }

  applyMobileRestoreQuery(query) {
    if (!this.isMobileSearch() || !query) return;

    this.input.value = query;
    this.input.defaultValue = query;
    this.input.setAttribute('value', query);
    this.searchTerm = query;
    this.toggleResetButton();
  }

  clearMobileSearchInputValue() {
    this.input.value = '';
    this.input.defaultValue = '';
    this.input.setAttribute('value', '');
    this.toggleResetButton();
  }

  clearMobileRestoreQueryReapply() {
    if (this.mobileRestoreQueryFrame) {
      cancelAnimationFrame(this.mobileRestoreQueryFrame);
      this.mobileRestoreQueryFrame = null;
    }

    this.mobileRestoreQueryTimers.forEach((timer) => clearTimeout(timer));
    this.mobileRestoreQueryTimers = [];
  }

  scheduleMobileRestoreQueryReapply(query) {
    this.clearMobileRestoreQueryReapply();

    const reapplyQuery = () => {
      const restoreState = this.getMobileRestoreState();
      if (!restoreState || restoreState.query !== query) return;
      if (!this.isMobileOverlaySearch()) return;

      const currentValue = this.getQuery();
      if (currentValue && currentValue !== query) return;

      this.applyMobileRestoreQuery(query);
    };

    this.mobileRestoreQueryFrame = requestAnimationFrame(() => {
      this.mobileRestoreQueryFrame = null;
      reapplyQuery();
    });

    this.mobileRestoreQueryTimers = MOBILE_RESTORE_REAPPLY_DELAYS.map((delay) =>
      setTimeout(reapplyQuery, delay)
    );
  }

  restoreMobileSearchState(restoreState) {
    if (!this.isMobileSearch()) return;
    if (restoreState.inputId && restoreState.inputId !== this.input.id) return;

    const details = this.closest('details');
    if (details && !details.hasAttribute('open')) details.setAttribute('open', true);

    this.closedByPageBlur = false;
    this.applyMobileRestoreQuery(restoreState.query);
    this.activateMobileOverlay();
    this.scheduleMobileRestoreQueryReapply(restoreState.query);

    const queryKey = this.getSearchResultsCacheKey(restoreState.query);
    const hasRenderedResults = Boolean(this.querySelector('#predictive-search-results-groups-wrapper'));

    if (hasRenderedResults) {
      this.setAttribute('results', true);
      this.open();
      return;
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.getSearchResults(restoreState.query);
  }

  activateMobileOverlay() {
    if (!this.isMobileSearch()) return;

    this.ensureMobileBackButton();
    this.classList.add('predictive-search--mobile-overlay');
    document.documentElement.classList.add('predictive-search-mobile-overlay-lock');
    document.body.classList.add('predictive-search-mobile-overlay-lock');
    this.updateMobileViewportHeight();
    this.bindMobileViewportListeners();
    this.bindMobileModalCloseButton();
  }

  deactivateMobileOverlay() {
    this.classList.remove('predictive-search--mobile-overlay');
    this.unbindMobileViewportListeners();
    this.unbindMobileModalCloseButton();
    this.style.removeProperty('--predictive-search-mobile-vh');

    if (!document.querySelector('predictive-search.predictive-search--mobile-overlay')) {
      document.documentElement.classList.remove('predictive-search-mobile-overlay-lock');
      document.body.classList.remove('predictive-search-mobile-overlay-lock');
    }
  }

  ensureMobileBackButton() {
    const form = this.input?.form;
    if (!form || form.querySelector(':scope > .predictive-search__mobile-back')) return;

    const button = document.createElement('button');
    const closeLabel =
      this.getAttribute('data-close-label') ||
      this.closest('details-modal')?.querySelector('.search-modal__close-button')?.getAttribute('aria-label') ||
      'Close search';

    button.type = 'button';
    button.className = 'predictive-search__mobile-back';
    button.setAttribute('aria-label', closeLabel);
    button.innerHTML = '&#8592;';

    form.insertBefore(button, form.firstElementChild);
  }

  bindMobileModalCloseButton() {
    const closeButton = this.closest('details-modal')?.querySelector('.search-modal__close-button');
    if (!closeButton || this.mobileModalCloseButton === closeButton) return;

    this.unbindMobileModalCloseButton();
    this.mobileModalCloseButton = closeButton;
    closeButton.addEventListener('click', this.onMobileModalCloseButtonClick, true);
  }

  unbindMobileModalCloseButton() {
    if (!this.mobileModalCloseButton) return;

    this.mobileModalCloseButton.removeEventListener('click', this.onMobileModalCloseButtonClick, true);
    this.mobileModalCloseButton = null;
  }

  clearMobileResults(clearSearchTerm = false, abortPendingRequest = true) {
    this.activateMobileOverlay();

    if (abortPendingRequest) {
      this.abortController.abort();
      this.abortController = new AbortController();
    } else if (this.abortController.signal.aborted) {
      this.abortController = new AbortController();
    }

    if (clearSearchTerm) this.clearMobileSearchInputValue();

    this.searchTerm = this.getQuery();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.isOpen = false;
    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.removeAttribute('style');
    this.updateMobileViewportHeight();
  }

  closeMobileSearch() {
    this.clearMobileRestoreState();
    this.input.blur();
    this.close(false);

    const detailsModal = this.closest('details-modal');
    if (detailsModal && typeof detailsModal.close === 'function') {
      detailsModal.close(false);
    }
  }

  onMobileModalCloseButtonClick(event) {
    if (!this.isMobileOverlaySearch()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.closeMobileSearch();
  }

  updateMobileViewportHeight() {
    if (!this.isMobileOverlaySearch()) return;

    const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    this.style.setProperty('--predictive-search-mobile-vh', `${Math.round(viewportHeight)}px`);
  }

  onMobileViewportChange() {
    if (!this.isMobileSearch()) {
      this.deactivateMobileOverlay();
      return;
    }

    if (this.mobileViewportUpdateFrame) cancelAnimationFrame(this.mobileViewportUpdateFrame);

    this.mobileViewportUpdateFrame = requestAnimationFrame(() => {
      this.mobileViewportUpdateFrame = null;
      this.updateMobileViewportHeight();
    });
  }

  bindMobileViewportListeners() {
    if (!this.isMobileOverlaySearch() || this.mobileViewportListenersActive) return;

    this.mobileViewportListenersActive = true;
    window.addEventListener('resize', this.onMobileViewportChange);
    window.visualViewport?.addEventListener('resize', this.onMobileViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onMobileViewportChange);
  }

  unbindMobileViewportListeners() {
    if (!this.mobileViewportListenersActive) return;

    this.mobileViewportListenersActive = false;
    window.removeEventListener('resize', this.onMobileViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onMobileViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onMobileViewportChange);

    if (this.mobileViewportUpdateFrame) {
      cancelAnimationFrame(this.mobileViewportUpdateFrame);
      this.mobileViewportUpdateFrame = null;
    }
  }

  shouldKeepMobileOverlayOpenOnBlur() {
    return this.isMobileOverlaySearch() && document.visibilityState === 'visible';
  }

  onChange() {
    super.onChange();
    this.closedByPageBlur = false;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.isMobileSearch()) {
        this.clearMobileRestoreState();
        this.clearMobileSearchInputValue();
        this.clearMobileResults();
        return;
      }

      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    if (this.isMobileSearch()) {
      event.preventDefault();
      if (!super.shouldResetForm()) return;

      this.clearMobileRestoreState();
      this.clearMobileSearchInputValue();
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.clearMobileResults();
      this.input.blur();
      return;
    }

    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.clearMobileRestoreState();
      this.clearMobileSearchInputValue();
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      if (this.isMobileSearch()) {
        this.clearMobileResults();
        return;
      }

      this.closeResults(true);
    }
  }

  onFocus() {
    this.closedByPageBlur = false;
    this.activateMobileOverlay();

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      if (this.shouldKeepMobileOverlayOpenOnBlur()) {
        this.updateMobileViewportHeight();
        return;
      }

      this.close();
    });
  }

  onPageBlur() {
    if (this.isMobileSearch()) {
      this.updateMobileViewportHeight();
      return;
    }

    this.closedByPageBlur = true;
    if (this.shouldKeepMobileOverlayOpenOnBlur()) {
      this.closedByPageBlur = false;
      this.updateMobileViewportHeight();
      return;
    }

    this.close(false);
  }

  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.closedByPageBlur = true;
      if (this.isMobileSearch()) {
        this.saveMobileRestoreState();
        this.updateMobileViewportHeight();
        return;
      }

      this.close(false);
    }
  }

  onPageHide() {
    if (!this.isMobileSearch()) return;

    this.saveMobileRestoreState();
    this.updateMobileViewportHeight();
  }

  onPageShow(event) {
    if (!this.isMobileSearch()) return;

    const restoreState = this.getMobileRestoreState();
    if (!restoreState) return;
    if (!this.isHistoryRestore(event) && !this.isMobileOverlaySearch()) return;

    this.restoreMobileSearchState(restoreState);
  }

  onMobileOverlayPointerDown(event) {
    if (!this.isMobileOverlaySearch()) return;
    if (!this.contains(event.target)) return;

    const interactiveTarget = event.target.closest(
      '.field, .predictive-search__mobile-back, a[href], button, input, textarea, select, [data-predictive-search-query]'
    );

    if (interactiveTarget) return;

    this.input.blur();
    this.updateMobileViewportHeight();
  }

  onSearchQueryTriggerClick(event) {
    const mobileBackButton = event.target.closest('.predictive-search__mobile-back');
    if (mobileBackButton && this.contains(mobileBackButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.closeMobileSearch();
      return;
    }

    const resultLink = event.target.closest('a[href]');
    if (resultLink && resultLink.closest('[data-predictive-search]') && this.contains(resultLink)) {
      this.saveMobileRestoreState();
      return;
    }

    const trigger = event.target.closest('[data-predictive-search-query]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    event.stopPropagation();

    const query = (trigger.getAttribute('data-predictive-search-query') || trigger.textContent || '').trim();
    const form = this.input?.form;
    if (!query || !form) return;

    this.clearMobileRestoreQueryReapply();
    this.input.value = query;
    this.searchTerm = query;
    this.toggleResetButton();

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    if (typeof form.submit === 'function') {
      form.submit();
      return;
    }

    const searchUrl = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const formData = new FormData(form);
    formData.set(this.input.name || 'q', query);
    searchUrl.search = '';
    formData.forEach((value, key) => {
      searchUrl.searchParams.set(key, value);
    });
    window.location.href = searchUrl.toString();
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      if (this.isMobileOverlaySearch()) {
        this.closeMobileSearch();
        return;
      }

      this.close(false);
      return;
    }
  }

  onKeyup() {
    if (!this.getQuery().length) {
      if (this.isMobileSearch()) return;

      this.close(true);
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getSearchResults(searchTerm) {
    if (this.isMobileSearch()) this.activateMobileOverlay();
    if (this.abortController.signal.aborted) this.abortController = new AbortController();

    const queryKey = this.getSearchResultsCacheKey(searchTerm);
    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[options][fields]': 'title,body,tag,product_type,variants.title,vendor,variants.sku',
    });

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const resultsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');

    this.setLiveRegionResults();

    if (!resultsWrapper) {
      this.removeAttribute('results');
      if (this.isMobileSearch()) {
        this.clearMobileResults(false, false);
        return;
      }

      this.close(false);
      return;
    }

    if (this.isMobileSearch()) {
      this.activateMobileOverlay();
      this.closedByPageBlur = false;
    }

    this.setAttribute('results', true);

    if (this.closedByPageBlur && !this.isMobileSearch()) {
      this.close(false);
      return;
    }
    this.closedByPageBlur = false;
    this.open();
  }

  setLiveRegionResults() {
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');

    this.removeAttribute('loading');
    if (liveRegionCount) this.setLiveRegionText(liveRegionCount.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.activateMobileOverlay();

    if (this.isMobileOverlaySearch()) {
      this.updateMobileViewportHeight();
      this.predictiveSearchResults.style.removeProperty('max-height');
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.deactivateMobileOverlay();

    if (clearSearchTerm) {
      this.clearMobileRestoreState();
      this.clearMobileSearchInputValue();
      this.removeAttribute('results');
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
